import React, { useRef, useState } from 'react';
import { ResultsPanel, type ParkingWithDistance } from './ResultsPanel';

// Fractions of viewport height. Starts at 30% per Q19.
const SNAP_POINTS_VH = [30, 60, 92];

interface ResultsBottomSheetProps {
  parkings: ParkingWithDistance[];
  carHeightM: number;
  highlightedId?: string | null;
  header?: React.ReactNode;
}

/** Draggable bottom sheet (mobile) — full map behind it, list of results inside. */
export const ResultsBottomSheet: React.FC<ResultsBottomSheetProps> = ({
  parkings,
  carHeightM,
  highlightedId,
  header,
}) => {
  const [snapIndex, setSnapIndex] = useState(0);
  const [dragHeightVh, setDragHeightVh] = useState<number | null>(null);
  const dragStart = useRef<{ y: number; heightVh: number } | null>(null);

  const heightVh = dragHeightVh ?? SNAP_POINTS_VH[snapIndex];

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    dragStart.current = { y: e.clientY, heightVh };
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragStart.current) return;
    const deltaVh = ((dragStart.current.y - e.clientY) / window.innerHeight) * 100;
    setDragHeightVh(Math.min(96, Math.max(12, dragStart.current.heightVh + deltaVh)));
  };

  const onPointerUp = () => {
    if (dragHeightVh != null) {
      let nearestIndex = 0;
      let nearestDist = Infinity;
      SNAP_POINTS_VH.forEach((point, i) => {
        const dist = Math.abs(point - dragHeightVh);
        if (dist < nearestDist) {
          nearestDist = dist;
          nearestIndex = i;
        }
      });
      setSnapIndex(nearestIndex);
    }
    setDragHeightVh(null);
    dragStart.current = null;
  };

  return (
    <div
      className="fixed left-0 right-0 bottom-0 bg-page border-t border-rule rounded-t-2xl shadow-[0_-4px_16px_rgba(0,0,0,0.08)] flex flex-col z-20 md:hidden"
      style={{
        height: `${heightVh}vh`,
        transition: dragHeightVh == null ? 'height 200ms ease-out' : 'none',
      }}
    >
      <div
        role="button"
        aria-label="Drag to resize results"
        className="flex items-center justify-center py-2.5 touch-none cursor-grab active:cursor-grabbing shrink-0"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
      >
        <div className="w-9 h-1 rounded-pill bg-rule" />
      </div>
      {header}
      <div className="flex-1 overflow-y-auto">
        <ResultsPanel parkings={parkings} carHeightM={carHeightM} highlightedId={highlightedId} />
      </div>
    </div>
  );
};
